import { HttpErrorResponse } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { IssueStatusName } from '@helios/ag-grid-config'
import {
  AccountClient,
  getUltClientData,
  HttpAccountService,
  RootIdType,
} from '@helios/common-api'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { CashBreakService } from '@helios/copilot'
import {
  AccountRowUpdateEvent,
  BreakUpdateEvent,
  EventType,
  GlobalMessagingService,
  HeliosMessageType,
  JobCompleteEvent,
  SessionMessagingService,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { BlankSessionService, SessionsService } from '@helios/sessions'
import {
  appendCommands,
  breaksBulkEditAction,
  Break as FocusedBreak,
  focusPanelAction,
  loadBreaksAction,
  PeriodLength,
  periodLengthToDate,
  RootState,
  selectActiveSession,
  selectActualReconDate,
  selectCwTransactionsSessionCacheStatus,
  selectFocusedTotalBreaks,
  selectSelectedReconDate,
  SessionCacheStatus,
  showBreakDetailsAction,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  ModalPositionService,
  UserPreferencesService,
} from '@helios/user-preferences'
import { compareSets, downloadExcelFile } from '@helios/utils'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import _ from 'lodash-es'
import { EMPTY, ReplaySubject, Subject } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { MultiBreaksKey } from '../panel-key'
import { BreaksValidationComponent } from './components/breaks-validation/breaks-validation.component'
import { BulkBreakModalComponent } from './components/bulk-break-modal/bulk-break-modal.component'
import { CreateIssueModalComponent } from './components/create-issue-modal/create-issue-modal.component'
import { FindMultiBreaksPopoverComponent } from './components/find-multi-breaks-popover/find-multi-breaks-popover.component'
import { HoldModalService } from './components/hold-modal/hold-modal.service'
import { MultiBreaksGridComponent } from './components/multi-breaks-grid/multi-breaks-grid.component'
import { NoteModalComponent } from './components/note-modal/note-modal.component'
import { ResolveModalComponent } from './components/resolve-modal/resolve-modal.component'
import {
  Break,
  BreakCodeResponse,
  BreakResponseObject,
  ExportBreaksToExcel,
  FindBreaksForm,
  PopOverFilter,
  resolveBreakCodes,
  UserFeatureAccess,
} from './model/break.model'
import { MultiBreaksService } from './services/multi-breaks.service'
import { breakToCashBreakInfo } from './utils/break-converter'

const MultiBreaksJobsFilterKey = `${MultiBreaksKey}/jobs`

@Component({
  selector: 'hs-multi-breaks',
  templateUrl: './multi-breaks-panel.component.html',
  styleUrls: ['./multi-breaks-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiBreaksPanelComponent
  extends BasePanelComponent
  implements OnInit, OnDestroy
{
  @ViewChild('multiBreaksGridComponent')
  readonly multiBreaksGridComponent?: MultiBreaksGridComponent
  @ViewChild('multiBreakPopover')
  readonly multiBreakPopover?: FindMultiBreaksPopoverComponent
  matchingBreaksCount = '0'
  data: Break[] = []
  breakCodes?: BreakCodeResponse
  isDownloadSubmitted = new ReplaySubject<boolean>(1)
  isLoading = false
  isJobCompleted = false
  previousDownloadForm: FindBreaksForm | undefined
  popOverConfig: FindBreaksForm | undefined
  totalFocusedBreaks$ = this.store.select(selectFocusedTotalBreaks)
  hasActiveSession$ = this.store.select(selectActiveSession).pipe(
    map(x => !!x && x.loadedAccountIds.length > 0),
    distinctUntilChanged(),
  )
  isInCurrentSession = true
  hasSameUltimateParent = true
  hasAccountId = false
  autoRefresh = false
  private skipLoadingBreaks = false

  updatedBreaks$: Subject<Break[]> = new Subject()
  ultClientData: AccountClient[] = []
  showInvalidPermissionsToGetBreaks = false
  private readonly destroySubject$ = new Subject<void>()
  /** @internal */
  _currentSessionAccountIds: number[] = []
  popoverAccounts: string[] | null = null
  accountType: string | undefined
  /** Recon date selected in the internal Accounts panel, if any. */
  reconDate: Date | undefined
  /**
   * The server's current recon date. Sessions started from a break fall back to
   * it when no Accounts-panel date is selected, which is always the case for
   * external users and assumed roles because they never get that panel.
   */
  actualReconDate: Date | undefined
  sessionCwTxCacheStatus$ = new Subject<SessionCacheStatus>()
  userFeatureAccess: Promise<UserFeatureAccess> =
    this.breaksService.getUserFeatureAccess()

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly breaksService: MultiBreaksService,
    private readonly store: Store<RootState>,
    private readonly actions$: Actions,
    private readonly modalService: NgbModal,
    private readonly blankSessionService: BlankSessionService,
    private readonly cashBreakService: CashBreakService,
    private readonly holdModalService: HoldModalService,
    private readonly sessionMessagingService: SessionMessagingService,
    private readonly globalMessagingService: GlobalMessagingService,
    private readonly httpAccountService: HttpAccountService,
    private readonly sessionsService: SessionsService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly toastService: ToastService,
    private readonly telemetry: TelemetryService,
    private readonly modalPositionService: ModalPositionService,
  ) {
    super(container)
    this.userPreferencesService
      .getAutoRefreshAccount()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((autoRefresh: boolean) => {
        this.autoRefresh = autoRefresh
      })
  }

  ngOnInit(): void {
    this.store
      .select(selectActiveSession)
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntil(this.destroySubject$),
        map(x => (x ? x.loadedAccountIds : [])),
      )
      .subscribe(async (accountIds: number[]) => {
        this._currentSessionAccountIds = accountIds

        if (accountIds.length > 0 && !this.skipLoadingBreaks) {
          await this.onSearchCurrentSession()
        } else if (accountIds.length > 0 && this.skipLoadingBreaks) {
          this.isInCurrentSession = false
        }

        this.hasAccountId = accountIds.length > 0
      })

    this.sessionMessagingService
      .on<unknown, BreakUpdateEvent>(
        EventType.MESSAGE,
        HeliosMessageType.BreakUpdate,
      )
      .pipe(map(x => x.payload))
      .subscribe(breakIds => this.applyBreakUpdated(breakIds))

    this.globalMessagingService
      .on<unknown, JobCompleteEvent>(
        EventType.MESSAGE,
        HeliosMessageType.JobComplete,
      )
      .pipe(
        takeUntil(this.destroySubject$),
        tap(event => this.handleJobCompleteEvent(event)),
      )
      .subscribe()

    this.globalMessagingService
      .on<unknown, AccountRowUpdateEvent<Record<string, string>>>(
        EventType.MESSAGE,
        HeliosMessageType.AccountUpdate,
      )
      .pipe(
        takeUntil(this.destroySubject$),
        tap(event => this.handleAccountUpdateEvent(event)),
      )
      .subscribe()

    this.store
      .select(selectSelectedReconDate)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(reconDate => {
        this.reconDate = reconDate
      })

    this.store
      .select(selectActualReconDate)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(actualReconDate => {
        this.actualReconDate = actualReconDate
      })

    this.store
      .select(selectCwTransactionsSessionCacheStatus)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(status => {
        this.sessionCwTxCacheStatus$.next(status)
      })

    this.actions$
      .pipe(takeUntil(this.destroySubject$), ofType(loadBreaksAction))
      .subscribe(({ payload: { type, ids } }) => {
        this.store.dispatch(focusPanelAction(MultiBreaksKey))
        if (type === 'accountIds') {
          this.popoverAccounts = (ids as number[]).map(id => id.toString())
        }
        this.onDownloadSubmit(
          {
            rootType: type,
            rootIds: type === 'accountIds' ? (ids as number[]) : [],
            searchIdType: 'assetIds',
            searchByIds: [],
            // My Accounts "Load Breaks" loads all statuses (Active/Held/Accepted)
            // by default (PVAL-25698); the breakIds deep-link path stays unfiltered.
            breakStatus:
              type === 'accountIds'
                ? [
                    IssueStatusName.FOUND,
                    IssueStatusName.WAITING,
                    IssueStatusName.IGNORED,
                  ]
                : [],
            breakPriority: [],
            breakTypes: [],
            breakIds: type === 'breakIds' ? (ids as string[]) : [],
            breakCategories: [],
          },
          true,
        )
      })
  }

  async applyBreakUpdated(breakIds: string[]) {
    const breakForm = {
      rootType: 'breakIds',
      rootIds: [],
      searchIdType: 'assetIds',
      searchByIds: [],
      breakStatus: [],
      breakPriority: [],
      breakTypes: [],
      breakIds: breakIds,
      breakCategories: [],
    }
    const breakData = await this.breaksService
      .getBreaks(breakForm)
      .pipe(map(x => x.data))
      .toPromise()

    // get ultimate parent ids for 'bulk edit'
    const acctIds = breakData?.map(x => x.acctId)
    const accountIdsSet = new Set(acctIds)
    const uniqueAccountIds = Array.from(accountIdsSet)

    const getUltParents = await this.httpAccountService
      .getAccountUltimateParentClients(uniqueAccountIds, RootIdType.account)
      .toPromise()

    this.ultClientData = getUltParents

    breakData?.forEach(breakItem => {
      breakItem.ultimateParentClientId = getUltParents.find(
        x => (x.accountId as unknown as number) === breakItem.acctId,
      )?.id as unknown as number
    })

    const updatedBreaks: Break[] = []
    breakData?.forEach(breakItem => {
      const index = this.data.findIndex(
        x => x.identifier === breakItem.identifier,
      )
      if (index > -1) {
        this.data[index] = breakItem
      }
      updatedBreaks.push(breakItem)
    })
    //We need to pass it down to the grid so the grid can update
    this.updatedBreaks$.next(updatedBreaks)
  }
  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.globalMessagingService.deleteFilters(MultiBreaksKey)
    this.globalMessagingService.deleteFilters(MultiBreaksJobsFilterKey)
  }

  async onDownloadSubmit(
    downloadForm: FindBreaksForm,
    forceLoadBreaks = false,
  ) {
    const skipBreaks = this.skipLoadingBreaks && !forceLoadBreaks
    this.skipLoadingBreaks = false
    if (skipBreaks) return
    if (
      downloadForm.rootIds.length === 0 &&
      downloadForm.searchByIds.length === 0 &&
      downloadForm.breakIds.length === 0
    ) {
      return
    }

    if (this.downloadFormIsForOneAccount(downloadForm)) {
      this.globalMessagingService.setFilters(MultiBreaksKey, [
        {
          jsonPath: '/payload/accountId',
          values: downloadForm.rootIds.map(id => id.toString()),
        },
      ])
      this.globalMessagingService.setFilters(MultiBreaksJobsFilterKey, [
        {
          jsonPath: '/payload/affectedAccountIds',
          values: downloadForm.rootIds.map(id => id.toString()),
        },
      ])
    } else {
      this.globalMessagingService.deleteFilters(MultiBreaksKey)
      this.globalMessagingService.deleteFilters(MultiBreaksJobsFilterKey)
    }

    this.isDownloadSubmitted.next(true)
    this.isLoading = true
    this.showInvalidPermissionsToGetBreaks = false
    this.previousDownloadForm = downloadForm

    // If the account ids match the current session account ids, then we are in the current session
    this.isInCurrentSession =
      downloadForm.rootType === 'accountIds' &&
      _.isEqual(downloadForm.rootIds, this._currentSessionAccountIds)

    try {
      const data = await this.breaksService
        .getBreaks(downloadForm)
        .pipe(
          tap(x => (this.matchingBreaksCount = x.matchingBreaksCount)),
          map(x => x.data),
          catchError((err: HttpErrorResponse) => {
            if (err.status === 403) {
              this.showInvalidPermissionsToGetBreaks = true
            }

            return EMPTY
          }),
        )
        .toPromise()

      if (!data) {
        return
      }

      // get ultimate parent ids for 'bulk edit'
      const acctIds = data?.map(x => x.acctId)
      const accountIdsSet = new Set(acctIds)
      const uniqueAccountIds = Array.from(accountIdsSet)

      const getUltParents = await this.httpAccountService
        .getAccountUltimateParentClients(uniqueAccountIds, RootIdType.account)
        .toPromise()

      this.ultClientData = getUltParents
      this.breakCodes = await this.breaksService
        .getBreakCodes(uniqueAccountIds)
        .toPromise()

      data?.forEach(breakItem => {
        breakItem.ultimateParentClientId = getUltParents.find(
          x => (x.accountId as unknown as number) === breakItem.acctId,
        )?.id as unknown as number
      })

      this.data = data
    } catch (error) {
      console.error('Error loading breaks data:', error)
    } finally {
      this.isLoading = false
    }
  }

  async onSearchCurrentSession() {
    const currentForm = this.multiBreakPopover?.getCurrentPopOverConfig()

    this.onDownloadSubmit(
      {
        rootType: currentForm?.rootType ?? 'accountIds',
        rootIds: this._currentSessionAccountIds,
        searchIdType: currentForm?.searchIdType ?? 'assetIds',
        searchByIds: currentForm?.searchByIds ?? [],
        breakStatus: currentForm?.breakStatus ?? [],
        breakPriority: currentForm?.breakPriority ?? [],
        breakTypes: currentForm?.breakTypes ?? [],
        breakIds: currentForm?.breakIds ?? [],
        breakCategories: currentForm?.breakCategories ?? [],
      },
      true,
    )
    this.popoverAccounts = this._currentSessionAccountIds.map(id => id + '')
    this.isInCurrentSession = true
  }

  onRefreshAfterJobCompleted() {
    if (this.previousDownloadForm) {
      this.onDownloadSubmit(this.previousDownloadForm, true)
    }
    this.isJobCompleted = false
  }

  async onUnaccept(breaks: Break[]) {
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const results = await this.breaksService
      .unresolve(breaks.map(x => x.identifier))
      .toPromise()

    this.handleActionResponseObject(results)
  }

  async onAccept(breaks: Break[]) {
    if (breaks.length > 1) {
      return this.openBulkBreakModal('accept', breaks)
    }
    const idBase = 'resolve-break'
    const uniqueId = `${idBase}-${Date.now()}`
    const modalRef = this.modalService.open(ResolveModalComponent, {
      size: 'lg',
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          idBase,
        )
      },
    } as NgbModalOptions)
    const component = modalRef.componentInstance as ResolveModalComponent
    component.breaks = breaks
    component.availability = resolveBreakCodes(breaks, this.breakCodes)

    this.modalPositionService.handleModalPositionSetup(uniqueId, idBase)

    let response: BreakResponseObject | null = null

    try {
      response = await modalRef.result
    } catch (error) {
      return // User closed the modal
    }

    this.handleActionResponseObject(response)
  }

  async onHold({ breaks, days }: { breaks: Break[]; days?: number }) {
    if (breaks.length > 1) {
      return this.openBulkBreakModal('hold', breaks, days)
    }
    let response: BreakResponseObject | null = null

    try {
      response = await this.holdModalService.toggleHoldModal(
        breaks,
        days,
        this.breakCodes,
      )
    } catch (error) {
      return // User closed the modal
    }

    this.handleActionResponseObject(response)
  }

  private async openBulkBreakModal(
    mode: 'accept' | 'hold',
    breaks: Break[],
    days?: number,
  ) {
    const idBase = `bulk-break-${mode}`
    const uniqueId = `${idBase}-${Date.now()}`
    const modalRef = this.modalService.open(BulkBreakModalComponent, {
      size: 'lg',
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          idBase,
        )
      },
    } as NgbModalOptions)
    const component = modalRef.componentInstance as BulkBreakModalComponent
    component.mode = mode
    component.breakCodes = this.breakCodes
    component.breaks = breaks
    if (mode === 'hold') {
      component.days = days
    }

    this.modalPositionService.handleModalPositionSetup(uniqueId, idBase)

    let response: BreakResponseObject | null = null
    try {
      response = await modalRef.result
    } catch (error) {
      return // User closed the modal
    }
    this.handleActionResponseObject(response)
  }

  async onEditNote(breaks: Break[]) {
    const idBase = 'issues-note'
    const uniqueId = `${idBase}-${Date.now()}`
    const modalRef = this.modalService.open(NoteModalComponent, {
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          idBase,
        )
      },
    } as NgbModalOptions)
    const modalComponent = modalRef.componentInstance as NoteModalComponent
    modalComponent.breaks = breaks
    modalComponent.breakCodes = this.breakCodes

    this.modalPositionService.handleModalPositionSetup(uniqueId, idBase)

    let response: BreakResponseObject | null = null

    try {
      response = await modalRef.result
    } catch (error) {
      return // modal closed
    }

    this.handleActionResponseObject(response)
  }

  async handleActionResponseObject(response: BreakResponseObject | null) {
    if (!response) return

    const commands = response.commands
    this.store.dispatch(
      appendCommands({
        commands,
      }),
    )
    // If we have non-successful validations, we open the breaks validation modal
    const failedValidationResults = response.breakValidationResults.filter(
      x => !x.successful,
    )
    if (failedValidationResults.length > 0) {
      const modalRef = this.modalService.open(BreaksValidationComponent, {
        keyboard: false,
      })
      modalRef.componentInstance.validationResults = failedValidationResults
    }
  }

  onShowDetails(breaks: Break[]) {
    if (breaks.length !== 1) return
    const breakModel = breaks[0]

    const issue = this.convertModel(breakModel)
    if (!issue) return

    setTimeout(() => {
      this.store.dispatch(showBreakDetailsAction(issue))
    }, 10)
  }

  onBulkEdit(breaks: Break[]) {
    const breakModels = breaks
    const issues = breakModels.map(breakModel => this.convertModel(breakModel))
    if (!issues) return

    setTimeout(() => {
      return this.store.dispatch(breaksBulkEditAction(issues as FocusedBreak[]))
    }, 10)
  }

  async onCreateIssue(breaks: Break[]) {
    const idBase = 'create-issue'
    const uniqueId = `${idBase}-${Date.now()}`
    const modalRef = this.modalService.open(CreateIssueModalComponent, {
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          idBase,
        )
      },
    } as NgbModalOptions)
    const modalComponent =
      modalRef.componentInstance as CreateIssueModalComponent
    modalComponent.breaks = breaks
    this.modalPositionService.handleModalPositionSetup(uniqueId, idBase)

    let response: BreakResponseObject | null = null

    try {
      response = await modalRef.result
    } catch (error) {
      return // modal closed
    }

    await this.handleActionResponseObject(response)
  }

  private convertModel(
    breakModel: Break | undefined,
  ): FocusedBreak | undefined {
    if (!breakModel) return undefined

    return {
      id: breakModel.identifier,
      metaData: breakModel.metaData,
      categoryName: breakModel.categoryName,
      message: breakModel.message,
      displayName: breakModel.displayName,
    } as FocusedBreak
  }

  async setLoadedAccountFromBreak({ accountIds }: { accountIds: number[] }) {
    // Skip loading breaks when the session is started from the breaks panel
    this.skipLoadingBreaks = true
    try {
      const accounts = await this.httpAccountService
        .getAccountTypeFromBreak(accountIds[0])
        .toPromise()
      const account = accounts.find(x => x.accountId === accountIds[0])
      this.accountType = account?.accountType as string

      // For breaks on sub-sleeves, load the parent portfolio session instead.
      const sessionAccountIds =
        account?.accountType === 'sleeveSimple' &&
        account.sleeveAggregateId != null
          ? [account.sleeveAggregateId]
          : accountIds

      this.createSession(sessionAccountIds)
    } catch (e) {
      this.skipLoadingBreaks = false
      throw e
    }
  }

  async setLoadedAccount({ accountIds }: { accountIds: number[] }) {
    const getAccountType = await this.httpAccountService
      .getAccountTypeFromBreak(accountIds[0])
      .toPromise()

    this.accountType = getAccountType.find(x => x.accountId === accountIds[0])
      ?.accountType as string

    this.createSession(accountIds)
  }

  private createSession(accountIds: number[]): void {
    this.sessionsService.create(
      accountIds,
      this.reconDate ?? this.actualReconDate ?? new Date(),
      periodLengthToDate(PeriodLength.Last100Days),
      false,
    )
  }

  async generateEmail(breakModel: Break) {
    window.copilot.addAssistantMessage({
      content: `Generating email for ${breakModel.displayName} break`,
    })
    window.copilot.startLoading()
    window.copilot.open()
    try {
      const breakEmailData = await this.breaksService
        .getBreakEmailData(breakModel.identifier)
        .toPromise()
      if (breakEmailData === undefined || breakEmailData === null) {
        window.copilot.addAssistantMessage({
          content: `Could not fetch email data`,
        })
        return
      }

      const emailTemplate = await window.copilot.getPresetTemplateByName(
        `Helios:${breakModel.displayName}`,
      )
      if (emailTemplate === undefined || emailTemplate === null) {
        window.copilot.addAssistantMessage({
          content: `No preset template found for Helios:${breakModel.displayName}`,
        })
        return
      }
      const modifiedEmailTemplate =
        emailTemplate?.prompt.replace(
          '{data}',
          JSON.stringify(breakEmailData),
        ) ?? ''

      await window.copilot.getApplicationChatResponse(modifiedEmailTemplate, {
        dsUsername: 'rubina',
      })
    } catch (e) {
      window.copilot.addAssistantMessage({
        content: 'Failed to generate email',
      })
    } finally {
      window.copilot.stopLoading()
      window.copilot.open()
    }
  }

  async resolveCashBreak(breakModel: Break) {
    if (!(await this.isValidCashBreak(breakModel))) return
    if (
      !this.isInCurrentSession ||
      !this._currentSessionAccountIds.includes(breakModel.acctId)
    ) {
      await this.setLoadedAccountAndNotify({
        accountIds: [breakModel.acctId],
      })
    }
    const cashBreakInfo = breakToCashBreakInfo(
      breakModel,
      getUltClientData(this.ultClientData, breakModel.acctId),
    )
    await this.cashBreakService.createCashBreakSuggestions(cashBreakInfo)
  }

  async openCashBreakSettings(breakModel: Break) {
    if (!(await this.isValidCashBreak(breakModel))) return
    const cashBreakInfo = breakToCashBreakInfo(
      breakModel,
      getUltClientData(this.ultClientData, breakModel.acctId),
    )
    const request =
      await this.cashBreakService.openCashBreakAdvancedSettingsModal(
        cashBreakInfo,
      )
    if (request) {
      if (
        !this.isInCurrentSession ||
        !this._currentSessionAccountIds.includes(breakModel.acctId)
      ) {
        await this.setLoadedAccountAndNotify({
          accountIds: [breakModel.acctId],
        })
      }
      await this.cashBreakService.createCashBreakSuggestionsWithRequest(
        cashBreakInfo,
        request,
      )
    }
  }

  private async isValidCashBreak(breakModel: Break) {
    if (breakModel.dataSourceTypes.length === 0) {
      console.error('No External DataSource Type found')
      return false
    }

    if (breakModel.dataSourceTypes.length > 1) {
      console.error('Multiple External DataSource Type found')
      return false
    }
    return true
  }

  initialSearchFilters(initialSearchFilters: FindBreaksForm) {
    this.multiBreakPopover?.setInitialFilters(initialSearchFilters)
  }

  resetPopOverFilter() {
    this.multiBreakPopover?.resetForm()
  }
  private handleJobCompleteEvent(event: JobCompleteEvent): void {
    if (this.skipLoadingBreaks) return
    if (!this.autoRefresh) {
      if (
        this.previousDownloadForm != null &&
        this.isAutoRefreshEnabled(event.payload.affectedAccountIds)
      ) {
        this.onDownloadSubmit(this.previousDownloadForm, true)
        return
      }

      const breakAcctIdUsed = this.data.map(item => Number(item.acctId))
      if (
        breakAcctIdUsed.length > 0 &&
        compareSets(
          new Set(breakAcctIdUsed),
          new Set(event.payload.affectedAccountIds),
        )
      ) {
        this.isJobCompleted = true
      }
    }
  }

  private handleAccountUpdateEvent(
    event: AccountRowUpdateEvent<Record<string, string>>,
  ): void {
    if (this.skipLoadingBreaks) return
    if (this.previousDownloadForm == null) return
    //If lastPerseusRun is being updated then the account just ran
    if (!event.payload.updatedFields.includes('lastPerseusRun')) return
    if (!this.isAutoRefreshEnabled([event.payload.accountId])) {
      return
    }

    this.onDownloadSubmit(this.previousDownloadForm, true)
  }

  private isAutoRefreshEnabled(potentiallyUpdatedAccounts: number[]): boolean {
    if (this.previousDownloadForm == null) {
      return false
    }
    if (!this.downloadFormIsForOneAccount(this.previousDownloadForm)) {
      return false
    }

    return potentiallyUpdatedAccounts.includes(
      this.previousDownloadForm.rootIds[0],
    )
  }

  private downloadFormIsForOneAccount(form: FindBreaksForm) {
    if (form.rootType !== 'accountIds') return false
    if (form.rootIds.length !== 1) return false

    return true
  }

  popoverFilterChanged(popOverConfig: FindBreaksForm) {
    this.skipLoadingBreaks = false
    this.popOverConfig = popOverConfig
  }
  async setLoadedAccountAndNotify({ accountIds }: { accountIds: number[] }) {
    await this.setLoadedAccount({ accountIds })

    return this.sessionCwTxCacheStatus$
      .pipe(
        filter(status => status === SessionCacheStatus.DONE),
        take(1),
      )
      .toPromise()
  }

  exportToExcel(colIdToHeaderNameMap: { [key: string]: string }) {
    const currentForm = this.multiBreakPopover?.getCurrentPopOverConfig()
    const findBreaksForm = this.popOverConfig ? this.popOverConfig : currentForm

    if (findBreaksForm == null) return

    let accountIdsValue: number[] = []
    let ultimateParentIdsValue: number[] = []
    let clientIdsValue: number[] = []
    let breakIds: string[] = []
    if (findBreaksForm.rootType === 'accountIds') {
      accountIdsValue = findBreaksForm.rootIds
    } else if (findBreaksForm.rootType === 'ultimateParentIds') {
      ultimateParentIdsValue = findBreaksForm.rootIds
    } else if (findBreaksForm.rootType === 'clientIds') {
      clientIdsValue = findBreaksForm.rootIds
    } else if (findBreaksForm.rootType === 'breakIds') {
      breakIds = findBreaksForm.breakIds
    }
    let assetIds: string[] | number[] = []
    let securityIds: string[] | number[] = []
    if (findBreaksForm.searchIdType === 'assetIds') {
      assetIds = findBreaksForm.searchByIds
    }
    if (findBreaksForm.searchIdType === 'securityIds') {
      securityIds = findBreaksForm.searchByIds
    }

    const filterForBreaks: PopOverFilter = {
      accountIds: accountIdsValue,
      clientIds: clientIdsValue,
      parentClientIds: ultimateParentIdsValue,
      assetIds: assetIds,
      securityIds: securityIds,
      breakTypes: findBreaksForm.breakTypes,
      breakStatuses: findBreaksForm.breakStatus,
      breakPriorities: findBreaksForm.breakPriority,
      breakIds: breakIds,
      breakCategories: findBreaksForm.breakCategories,
    }

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const request: ExportBreaksToExcel = {
      filter: filterForBreaks,
      headersMap: colIdToHeaderNameMap,
      userTimeZone: userTimeZone,
    }
    this.breaksService.exportBreaks(request).subscribe(
      response => {
        downloadExcelFile(response, 'Breaks')
        this.toastService.success(
          "Export to Excel completed successfully. The exported file is available in your browser's Downloads",
          {
            closeButton: true,
            timeOut: 3000,
          },
        )
      },
      () => {
        this.toastService.error(
          'Export to Excel failed. Please try again.',
          '',
          {
            closeButton: true,
            timeOut: 3000,
            disableTimeOut: false,
          },
        )
      },
    )
    this.toastService.warning(
      'Export to Excel is in progress. It may take more than 30 seconds. Please wait.',
      {
        closeButton: true,
        timeOut: 3000,
      },
      'In Progress',
    )
  }
}
